"use client";

import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type InputHTMLAttributes,
  type RefObject,
} from "react";

const CHECK_PATH = "M3.5 8.2 6.4 11.1 12.5 4.5";
const CHECK_DRAW_EASE = "cubic-bezier(0.65, 0, 0.35, 1)";

function usePrefersReducedMotion(): boolean {
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReduceMotion(media.matches);
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);

  return reduceMotion;
}

function useAnimatedCheckStroke(
  pathRef: RefObject<SVGPathElement | null>,
  checked: boolean,
  reduceMotion: boolean,
) {
  useLayoutEffect(() => {
    let cancelled = false;
    let frameId = 0;

    const applyStroke = () => {
      const path = pathRef.current;
      if (!path || cancelled) return;

      const length = path.getTotalLength();
      if (!Number.isFinite(length) || length <= 0) {
        frameId = window.requestAnimationFrame(applyStroke);
        return;
      }

      path.style.strokeDasharray = String(length);
      path.style.transition = reduceMotion
        ? "none"
        : `stroke-dashoffset 0.22s ${CHECK_DRAW_EASE}, opacity 0.08s ease`;
      path.style.strokeDashoffset = checked ? "0" : String(length);
      path.style.opacity = checked ? "1" : "0";
    };

    applyStroke();

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frameId);
    };
  }, [checked, reduceMotion]);
}

type CcAnimatedCheckmarkProps = {
  checked: boolean;
  className?: string;
};

function CcAnimatedCheckmark({ checked, className }: CcAnimatedCheckmarkProps) {
  const pathRef = useRef<SVGPathElement>(null);
  const reduceMotion = usePrefersReducedMotion();
  useAnimatedCheckStroke(pathRef, checked, reduceMotion);

  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        ref={pathRef}
        d={CHECK_PATH}
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
        className="cc-animated-checkbox__path"
      />
    </svg>
  );
}

export type CcAnimatedCheckboxProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "type" | "checked" | "onChange" | "className"
> & {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  inputClassName?: string;
  className?: string;
};

/** Native checkbox with brand fill + animated check stroke (Cognis storefront parity). */
export function CcAnimatedCheckbox({
  checked,
  onCheckedChange,
  className = "cc-animated-checkbox",
  inputClassName = "cc-animated-checkbox__input",
  id,
  ...rest
}: CcAnimatedCheckboxProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  return (
    <span className={className}>
      <input
        {...rest}
        id={inputId}
        type="checkbox"
        className={inputClassName}
        checked={checked}
        onChange={(event) => onCheckedChange(event.target.checked)}
      />
      <span className="cc-animated-checkbox__mark" aria-hidden>
        <CcAnimatedCheckmark checked={checked} />
      </span>
    </span>
  );
}
